/* eslint-disable no-unsafe-optional-chaining */
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';

import { changeDateFormate } from '../../../utils/dateFormat';
import { exportExcel } from '../../../utils/exportToExcel';
import { Numbers, monthNames } from '../../../utils/typeConstants';
import { useGetTriadQuery, useGetTriadTlGraphicHistoryQuery } from './triad.slice';

type TriadGraphicHistoryProps = {
    empNo: string;
    year: number;
    month: number;
    viewAnswers: (currTriad: any) => void;
};

export const TriadGraphicHistory = ({ empNo, year, month, viewAnswers }: TriadGraphicHistoryProps) => {
    const [triadId, setTriadId] = useState<number | string>('');
    const [basicData, setBasicData] = useState<any>();
    const [monthHistory, setMonthHistory] = useState<number>(0);
    const graphHistory = [
        { name: 'current month', value: 0 },
        { name: 'Prior 1 month(s)', value: 1 },
        { name: 'Prior 2 month(s)', value: 2 },
        { name: 'Prior 3 month(s)', value: 3 },
        { name: 'Prior 4 month(s)', value: 4 },
        { name: 'Prior 5 month(s)', value: 5 },
        { name: 'Prior 6 month(s)', value: 6 },
    ];
    const { data: triadsData } = useGetTriadQuery({ triadId }, { skip: !triadId });

    const { data: graphicHistory } = useGetTriadTlGraphicHistoryQuery(
        {
            empNo,
            year,
            month,
            monthsDuration: monthHistory,
        },
        {
            skip: !empNo,
        }
    );

    useEffect(() => {
        if (triadsData?.data?.length > 0) {
            viewAnswers(triadsData.data[0]);
        }
    }, [triadsData]);

    const basicOptions = {
        maintainAspectRatio: false,
        aspectRatio: 1.5,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem: any) => {
                        const dataset = tooltipItem.dataset;
                        const index = tooltipItem.dataIndex;
                        const value = dataset.data[index] + '%';
                        const employee = dataset.employee[index];
                        const month = dataset.monthYear[index];
                        return [`${dataset.label}: ${value}`, `Employee: ${employee}`, `Month: ${month}`];
                    },
                },
            },
        },
        onClick: (event: any, elements: any) => {
            if (elements.length > 0) {
                const datasetIndex = elements[0].datasetIndex;
                const dataIndex = elements[0].index;
                const triadId = basicData?.datasets[datasetIndex].triadId[dataIndex];
                setTriadId(triadId);
            }
        },
        scales: {
            x: {
                display: true,
                min: 0,
                ticks: {
                    color: '#495057',
                },
                grid: {
                    display: false,
                },
            },
            y: {
                type: 'linear',
                display: true,
                text: 'Coaching Observations',
                position: 'left',
                grid: {
                    display: false,
                },
                title: {
                    display: true,
                    text: 'Score',
                    color: '#1A5AA5',
                    font: {
                        size: 14,
                        weight: 'bold',
                    },
                },
            },
        },
    };

    useEffect(() => {
        if (graphicHistory?.data?.length > 0) {
            const triadScore = graphicHistory?.data?.map((item: any) => {
                return (item?.assessment_score * 100).toFixed(2);
            });
            const triadTargetScore = graphicHistory?.data?.map((item: any) => {
                return (item?.tl_avg_asse_score * 100).toFixed(2);
            });

            const data = {
                labels: graphicHistory?.data?.map((item: any) => changeDateFormate(item.create_date)),
                datasets: [
                    {
                        data: triadTargetScore,
                        type: 'line',
                        label: 'Average Score',
                        borderColor: '#00B6B2',
                        borderWidth: Numbers.TWO,
                        pointRadius: 0.9,
                        fill: false,
                        tension: 0.4,
                        employee: graphicHistory?.data?.map((item: any) => item.associate),
                        monthYear: graphicHistory?.data?.map(
                            (item: any) => monthNames[Number(item.month)] + ', ' + item.year
                        ),
                        triadId: graphicHistory?.data?.map((item: any) => item.triad_id),
                    },
                    {
                        data: triadScore,
                        fill: true,
                        label: 'Assessment Score',
                        backgroundColor: '#77F7C1',
                        borderWidth: Numbers.ONE,
                        borderRadius: Numbers.FIVE,
                        employee: graphicHistory?.data?.map((item: any) => item.associate),
                        monthYear: graphicHistory?.data?.map(
                            (item: any) => monthNames[Number(item.month)] + ', ' + item.year
                        ),
                        triadId: graphicHistory?.data?.map((item: any) => item.triad_id),
                    },
                ],
            };
            setBasicData(data);
        } else setBasicData([]);
    }, [graphicHistory]);

    const ExportScoreHistory = () => {
        const historyData = graphicHistory?.data?.map((item: any) => ({
            Manager: item.manager,
            TeamLead: item.team_lead,
            TLAvgScore: item.tl_avg_asse_score,
            Associate: item.associate,
            AssessmentScore: item.assessment_score,
            Date: item.create_date,
            Month: item.month,
            Year: item.year,
            Acknowledged: item.acknowledged,
        }));
        exportExcel(historyData, 'Coaching Observations Score History');
    };

    return (
        <div>
            <div className="row mt-4">
                <div className="col-md-5">
                    <div className="content" data-testid="select-team">
                        <label style={{ textAlign: 'start' }} htmlFor="select-team">
                            History
                        </label>
                        <Dropdown
                            id="ddlHistory"
                            placeholder="Select a duration"
                            className="custom-dropdown"
                            options={graphHistory}
                            optionValue={'value'}
                            optionLabel={'name'}
                            value={monthHistory}
                            onChange={(e: any) => {
                                setMonthHistory(e.value);
                            }}
                        />
                    </div>
                </div>
                <div className="col-md-7 mt-4">
                    <Button
                        type="button"
                        icon="pi pi-upload me-2"
                        className="btn-export float-end"
                        onClick={ExportScoreHistory}
                        data-pr-tooltip="XLS"
                    >
                        Export
                    </Button>
                </div>
            </div>
            <div className="col-md-12 mt-4" style={{ fontSize: 'larger', color: '#00B6B2' }}>
                Coaching Observations Scores
            </div>
            <Chart type="bar" data={basicData} options={basicOptions} />
        </div>
    );
};
